import {
  getQueryKey,
  queryClient,
  useApi,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { getInputUrl } from "./playInputs"
import { getPoolUrl } from "./playPools"
import type { InputParams, StreamParams, VirtualAssetParams, VirtualProgramParams } from "./types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/streams/?org=${orgUuid} */
export const getStreamListUrl = ({ orgUuid, poolUuid, inputUuid }: Required<InputParams>) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/streams/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/?org=${orgUuid} */
export const getStreamUrl = ({
  orgUuid,
  poolUuid,
  inputUuid,
  streamUuid
}: Required<StreamParams>) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/psi/?org=${orgUuid} */
export const getStreamPsiUrl = ({
  orgUuid,
  poolUuid,
  inputUuid,
  streamUuid
}: Required<StreamParams>) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/psi/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/assets/${assetUuid}/?org=${orgUuid} */
export const getVirtualAssetUrl = ({
  orgUuid,
  poolUuid,
  inputUuid,
  streamUuid,
  assetUuid
}: Required<VirtualAssetParams>) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/assets/${assetUuid}/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/assets/?org=${orgUuid} */
export const getVirtualAssetListUrl = ({
  orgUuid,
  poolUuid,
  inputUuid,
  streamUuid
}: Required<StreamParams>) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/assets/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/programs/?org=${orgUuid}&start_date=${startDate}&duration=${duration} */
export const getVirtualProgramListUrl = ({
  orgUuid,
  poolUuid,
  inputUuid,
  streamUuid,
  startDate,
  duration
}: Required<Omit<VirtualProgramParams, "startDate" | "duration">> &
  Pick<VirtualProgramParams, "startDate" | "duration">) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/programs/?org=${orgUuid}` +
  (startDate ? `&start_date=${toPageDate(new Date(startDate))}` : "") +
  (duration ? `&duration=${duration}` : "")

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/programs/import/?org=${orgUuid}&start_date=${startDate}&duration=${duration} */
export const importVirtualProgramUrl = ({
  orgUuid,
  poolUuid,
  inputUuid,
  streamUuid,
  startDate,
  duration
}: Required<Omit<VirtualProgramParams, "startDate" | "duration">> &
  Pick<VirtualProgramParams, "startDate" | "duration">) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/programs/import/?org=${orgUuid}` +
  (startDate ? `&start_date=${toPageDate(new Date(startDate))}` : "") +
  (duration ? `&duration=${duration}` : "")

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useStream = (params?: StreamParams) => {
  const mergedParams = useRouteParams(params)
  const url = getStreamUrl(mergedParams)

  return useGetQuery<api.ott.InputStream>("ott", url)
}

export const useUpdateStream = (params?: StreamParams) => {
  const mergedParams = useRouteParams(params)
  const url = getStreamUrl(mergedParams)
  const inputUrl = getInputUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.InputStreamRequest>
  type TResponse = api.ott.InputStream

  return usePatchQuery<TRequest, TResponse>("ott", url, [inputUrl, poolUrl])
}

export const useStreamPsi = (params?: StreamParams) => {
  const mergedParams = useRouteParams(params)
  const url = getStreamPsiUrl(mergedParams)

  return useGetQuery<api.ott.InputStreamPsi>("ott", url)
}

export const useVirtualAsset = (params?: VirtualAssetParams) => {
  const mergedParams = useRouteParams(params)
  const url = getVirtualAssetUrl(mergedParams)

  return useGetQuery<api.ott.VirtualAsset>("ott", url)
}

export const useVirtualAssetsList = (params?: StreamParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getVirtualAssetListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedVirtualAssetList>("ott", listUrl)
}

export const useAddVirtualAsset = (params?: StreamParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getVirtualAssetListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.VirtualAsset>
  type TResponse = api.ott.VirtualAsset

  return usePostQuery<TRequest, TResponse>("ott", listUrl, [listUrl])
}

export const useUpdateVirtualAsset = (params?: VirtualAssetParams) => {
  const mergedParams = useRouteParams(params)
  const url = getVirtualAssetUrl(mergedParams)
  const listUrl = getVirtualAssetListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.VirtualAsset>
  type TResponse = api.ott.VirtualAsset

  return usePatchQuery<TRequest, TResponse>("ott", url, [listUrl])
}

export const useDeleteVirtualAsset = (params?: VirtualAssetParams) => {
  const { jsonDelete } = useApi("ott")
  const mergedParams = useRouteParams(params)
  const url = getVirtualAssetUrl(mergedParams)
  const assetListUrl = getVirtualAssetListUrl(mergedParams)
  const programListUrl = getVirtualProgramListUrl(mergedParams)

  return {
    mutationFn: () => jsonDelete(url),
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("ott", assetListUrl) }),
        queryClient.invalidateQueries({ queryKey: getQueryKey("ott", programListUrl) })
      ])

      setTimeout(() => queryClient.removeQueries({ queryKey: getQueryKey("ott", url) }), 1_000)
    }
  }
}

export const useVirtualPrograms = (params: VirtualProgramParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getVirtualProgramListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedVirtualProgramList>("ott", listUrl)
}

export const useImportVirtualPrograms = (params: VirtualProgramParams) => {
  const { jsonPost } = useApi("ott")
  const mergedParams = useRouteParams(params)
  const url = importVirtualProgramUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.VirtualProgram[]>
  type TResponse = api.ott.VirtualProgram[]

  return {
    mutationFn: (data: TRequest) => jsonPost<TResponse>(url, data),
    onSuccess: async (data: TResponse) => {
      const result: api.ott.PaginatedVirtualProgramList = {
        duration: params.duration,
        results: data,
        startDate: toPageDate(params.startDate)
      }

      queryClient.setQueryData(getQueryKey("ott", url), result)
    }
  }
}

//--------------------------------------------------------------------------------------------------
// Helpers
//--------------------------------------------------------------------------------------------------

const toPageDate = (date?: Date) => (date ? `${date.toISOString().substring(0, 19)}Z` : undefined)
